import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import posthog from "posthog-js";
import { useStore } from "store";
import CONFIG from "config/config";
import { CB_EVENTS } from "campaign-builder/analytics/cbAnalytics";
import { CampaignSubmissionModal } from "components/CampaignSubmissionModal";
import { SuccessIcon } from "components/icons/components/SuccessIcon";
import { EDDM_SIGNUP } from "shared/constants";
import CheckoutSidebar from "campaign-builder/checkout/sidebar";
import { getChannelConfig, type BuilderStep } from "campaign-builder/config";
import { getPostSubmitUrl } from "campaign-builder/postSubmitUrl";
import { BuilderShell, BuilderPageSkeleton, BuilderErrorState, useLoadTimeout } from "campaign-builder/shell";
import { ThemedOadsSidebar } from "campaign-builder/ThemedOadsSidebar";
import { oadsSidebarFooterItems } from "campaign-builder/oadsSidebarFooter";
import {
  isLaunchDateOutdated,
  deriveStepState,
  deriveLastActiveStep,
  resumeStepFromCursor,
  BUILDER_STEPS,
} from "campaign-builder/state";
import { updateCampaignExtraData } from "graphQL/mutations/campaignExtraData";
import { isClientEmailVerified } from "graphQL/mutations/user";
import { useGetFlyerInfo } from "pages/flyer/flyerTypePage/useGetFlyerInfo";
import type { AdmBuilderSnapshot } from "campaign-builder/channels/directmail/adm/adm.config";
import { useCampaignData } from "campaign-builder/context/CampaignDataContext";
import { useClientConfig } from "campaign-builder/context/ClientConfigContext";
import { useCampaignCostPreviewCalculation } from "campaign-builder/hooks/useCampaignCostPreviewCalculation";
import { SaveStatusProvider, type SaveStatus } from "./saveStatus";
import { LaunchDateFocusContext } from "./launchDateFocus";
import { TargetingValidationContext, validateCampaignName } from "./targetingValidation";
import { useAdmCapabilities } from "./admCountryCapabilities";
import { DesignSelectionContext } from "./designSelection";
import { TrackingValidityContext } from "./trackingState";
import { AdmTargetingStep } from "./targeting/AdmTargetingStep";
import { AdmDesignStep } from "./design/AdmDesignStep";
import { AdmTrackingStep } from "./tracking/AdmTrackingStep";
import { UserMenu } from "./UserMenu";
import { useHydrateSelectedDesign } from "./design/useHydrateSelectedDesign";
import { designStepHasAddressBox, lacksAddressBox } from "./design/addressBoxGate";
import { OrderDetailsDrawer } from "./order-details/OrderDetailsDrawer";
import { useBillingGateSurface } from "campaign-builder/checkout/billing-profile/useBillingGateSurface";
import type { DiscountChoice } from "./order-details/reviewOffer";
import { useADMSubmit } from "campaign-builder/submit/useADMSubmit";
import { ConfirmEmailModal } from "./order-details/ConfirmEmailModal";
import CheckoutContent from "campaign-builder/checkout/CheckoutContent";
import type { DesignView } from "./design/types";

const formatTotal = (value: number) =>
  `$${(value || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Copilot anchor for the shared SummaryCard's CTA, one per step (WP-H4) — the
// button's label already varies by step (see SummaryCard's CTA_LABELS); this
// keeps the id equally step-specific so a driving agent never has to resolve
// which step's "Continue" it's clicking.
const ADM_CTA_DATA_CY: Record<BuilderStep, string> = {
  targeting: "admContinueToDesignButton",
  design: "admContinueToTrackingButton",
  tracking: "admContinueToSubmitButton",
};

export const AdmBuilder = () => {
  const { campaignId, clientId } = useParams<{ campaignId: string; clientId: string }>();
  const {
    campaignData,
    activeAudienceDetails,
    recipientsAmount,
    isLoading,
    error,
    setCampaignId,
    setClientId,
    refetchCampaign,
    isReadOnly,
    updateCampaignState,
    audiencesLoading,
    audiencesError,
    refetchAudiences,
    campaignAudiencesLoading,
    campaignAudiencesError,
    refetchCampaignAudiences,
  } = useCampaignData();
  const { country, client, isLoading: isClientConfigLoading } = useClientConfig();
  const { campaignCostPreview } = useCampaignCostPreviewCalculation({ format: campaignData?.flyerType });
  const admConfig = useMemo(() => getChannelConfig("ADM"), []);
  const { user, updateUser } = useStore();
  const { pathname } = useLocation();

  // The oads-sidebar menu spans all three frontends. From the builder, cross-frontend
  // items (platform / next) are absolute URLs → full-page <a>; builder-local routes
  // navigate client-side via react-router.
  const renderSidebarLink = useCallback(
    ({ href, className, children }: { href: string; className?: string; children: React.ReactNode }) => {
      // Normalize a possible trailing slash on the base so stripping it keeps the
      // leading "/" of the route (else react-router treats it as a relative path).
      const builderBase = CONFIG.BUILDER_BASE_URL.replace(/\/+$/, "");
      const isExternal = /^https?:\/\//i.test(href) && !href.startsWith(builderBase);
      if (isExternal) {
        return (
          <a href={href} className={className}>
            {children}
          </a>
        );
      }
      let to = href.startsWith(builderBase) ? href.slice(builderBase.length) : href;
      if (!to.startsWith("/")) to = `/${to}`;
      return (
        <Link to={to} className={className}>
          {children}
        </Link>
      );
    },
    []
  );
  const [activeStep, setActiveStep] = useState<BuilderStep>("targeting");
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  // Billing-profile gate, shown on every builder step.
  const billingGate = useBillingGateSurface(clientId);
  const [launchDateFocusVersion, setLaunchDateFocusVersion] = useState(0);
  const launchDateFocusPending = useRef(false);
  const [validationVersion, setValidationVersion] = useState(0);
  const [selectedDesign, setSelectedDesign] = useState<DesignView | null>(null);
  const [trackingValid, setTrackingValid] = useState(true);
  const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);
  const [isCampaignSubmissionModalOpen, setIsCampaignSubmissionModalOpen] = useState(false);
  const [isConfirmEmailOpen, setIsConfirmEmailOpen] = useState(false);
  // HUB-458: lifted from OrderDetailsDrawer's useDiscountExclusivity so
  // useADMSubmit can send it as `discount_choice` at checkout.
  const [discountChoice, setDiscountChoice] = useState<DiscountChoice>(null);

  // PAD-32157 — Declarative capture helper. Fires when a user action commits
  // to entering a step (handleStepChange, the resume effect on initial load,
  // handleStepBack from the sidebar), NOT via a useEffect that observes
  // `activeStep` mutations. Follows "action → reaction" — every step
  // transition routes through one function that emits the milestone.
  const captureStepReached = useCallback(
    (step: BuilderStep) => {
      if (!campaignId || !clientId) return;
      const base = { campaign_id: campaignId, client_id: clientId };
      if (step === "targeting") posthog.capture(CB_EVENTS.AUDIENCE_REACHED, base);
      else if (step === "design") posthog.capture(CB_EVENTS.DESIGN_REACHED, base);
      else if (step === "tracking") {
        posthog.capture(CB_EVENTS.SUBMIT_REACHED, {
          ...base,
          total_cost: campaignCostPreview?.total ?? 0,
        });
      }
    },
    [campaignId, clientId, campaignCostPreview?.total]
  );

  // Wraps setActiveStep so every user-initiated step transition emits the
  // milestone through the shared capture path — no imperative useEffect on
  // activeStep required.
  const handleStepChange = useCallback(
    (next: BuilderStep) => {
      if (next === activeStep) return;
      setActiveStep(next);
      captureStepReached(next);
    },
    [activeStep, captureStepReached]
  );

  const isPaymentSkipped = !!(campaignData?.isPaymentSkipped || client?.isSkipCheckoutEnabled);

  // The submit hook owns the submit/skip decision, the server-authoritative
  // breakdown, and the Stripe redirect-back handler.
  const {
    onSubmit,
    showCheckout,
    setShowCheckout,
    costBreakdown,
    breakdownReady,
    couponCode,
    applyReviewDiscount,
    campaignId: submitCampaignId,
    countryCode,
  } = useADMSubmit({ setIsCampaignSubmissionModalOpen, discountChoice });
  const admCapabilities = useAdmCapabilities();

  // The terms acknowledgement is now the inline note under the drawer's button
  // (TermsAgreementNote), so this goes straight to submission.
  const handleGoToPayment = useCallback(async () => {
    if (isReadOnly) return;
    setOrderDetailsOpen(false);
    await onSubmit();
  }, [isReadOnly, onSubmit]);

  // The store's isVerified isn't hydrated in the builder, so check the real status.
  const handleReviewAndFinish = useCallback(async () => {
    if (user?.isVerified === true) {
      setOrderDetailsOpen(true);
      return;
    }
    try {
      const response = await isClientEmailVerified();
      const {
        data: { getClientEmailVerified },
      } = await (response as Response).json();
      if (getClientEmailVerified?.success) {
        updateUser({ isVerified: true });
        setOrderDetailsOpen(true);
        return;
      }
    } catch (error) {
      // fall through to the modal rather than bypass the gate
    }
    setIsConfirmEmailOpen(true);
  }, [user?.isVerified, updateUser]);

  const { flyerInfo, isLoading: isFlyerLoading } = useGetFlyerInfo();

  // Hydrate the selected design for DISPLAY only (gallery highlight + tracking
  // placeholders). Step navigation does NOT depend on this — see `hasDesign`.
  useHydrateSelectedDesign({ clientId, selectedDesign, setSelectedDesign });

  // A campaign "has a design" if there's an in-session selection (immediate) OR an
  // assigned design id from flyer info (reliable on reload). The OR matters: flyerInfo
  // is NOT refetched after activateDesign, so right after picking a design only
  // `selectedDesign` reflects it — without this, "Continue to Tracking" stayed blocked
  // until a reload. activeRoomId still covers the reload case with no hydration wait.
  const hasDesign = !!selectedDesign || !!flyerInfo?.activeRoomId;

  // All campaign data the step predicates read has loaded. Selected audiences come
  // from TWO queries (the campaign's audiences + the audience list), so both must
  // settle before recipient counts are trustworthy — deriving earlier is what used
  // to bounce the user back to targeting.
  const stillLoading =
    isLoading || isClientConfigLoading || isFlyerLoading || audiencesLoading || campaignAudiencesLoading;
  const dataLoaded = !stillLoading && !!campaignData;

  // PAD-31535 — a fetch failure (campaign, audiences, or campaign-audiences) or a
  // hung load resolves to the shared error state instead of a skeleton that never
  // clears. `dataLoaded` alone never checked these, so any failure froze the page.
  const loadTimedOut = useLoadTimeout(stillLoading);
  const hasError = !!error || !!audiencesError || !!campaignAudiencesError || loadTimedOut;
  // Retry the data-context fetches that expose refetch handles (campaign + audiences).
  // Client-config and flyer-info also feed the timeout but have no refetch handle, so
  // a hang in those resolves on a full reload rather than via this button.
  const handleRetry = useCallback(() => {
    void refetchCampaign();
    refetchAudiences();
    refetchCampaignAudiences();
  }, [refetchCampaign, refetchAudiences, refetchCampaignAudiences]);

  const addressBoxRequired = !!admConfig.design.addressBoxRequired && admCapabilities.addressBoxRequired;

  const snapshot: AdmBuilderSnapshot = useMemo(
    () => ({
      id: campaignData?.id ?? null,
      subtype: campaignData?.subtype,
      flyerType: campaignData?.flyerType,
      startDate: campaignData?.startDate,
      campaignName: campaignData?.campaignName,
      audienceCount: activeAudienceDetails.length,
      recipientsAmount,
      countryCode,
      minRecipients: admCapabilities.minRecipients,
      deliveryRuleSelected: typeof campaignData?.updateAddressOnMove === "boolean",
      returnAddressSelected: campaignData?.returnAddressId != null,
      hasDesign,
      // PAD-32395: a selected design without an address box leaves the design step incomplete, so Continue is blocked.
      designHasAddressBox: designStepHasAddressBox({
        required: addressBoxRequired,
        hasDesign,
        selectedDesign,
      }),
      trackingValid,
    }),
    [
      campaignData,
      activeAudienceDetails.length,
      recipientsAmount,
      countryCode,
      admCapabilities.minRecipients,
      hasDesign,
      selectedDesign,
      addressBoxRequired,
      trackingValid,
    ]
  );

  const stepRestored = useRef(false);
  useEffect(() => {
    if (stepRestored.current || !dataLoaded) return;
    stepRestored.current = true;
    const derived = deriveStepState(snapshot, admConfig).currentStep;
    const cursor = admConfig.lastActiveStep;
    const fromCursor = cursor ? resumeStepFromCursor(campaignData?.lastActiveStep ?? null, cursor) : derived;
    const furthest = BUILDER_STEPS.indexOf(fromCursor) > BUILDER_STEPS.indexOf(derived) ? fromCursor : derived;
    setActiveStep(furthest);
    // PAD-32157 — emit the milestone for the resumed step through the shared
    // capture path so the analytics emit is single-source across all entrypoints.
    captureStepReached(furthest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLoaded]);

  useEffect(() => {
    if (!dataLoaded || !campaignData || isReadOnly) return;
    const cursor = admConfig.lastActiveStep;
    if (!cursor) return;
    const stored = campaignData.lastActiveStep ?? null;
    if (cursor.terminalValues?.includes(stored ?? "")) return;

    const { completion } = deriveStepState(snapshot, admConfig);
    const next = deriveLastActiveStep(completion, cursor);
    const order = [cursor.initial, ...BUILDER_STEPS.map((step) => cursor.byCompletedStep[step]).filter(Boolean)];
    if (order.indexOf(next) > order.indexOf(stored ?? cursor.initial)) {
      updateCampaignExtraData({ campaignId: campaignData.id, lastActiveStep: next }).catch(() => undefined);
      updateCampaignState({ ...campaignData, lastActiveStep: next });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLoaded, isReadOnly, snapshot, admConfig]);

  const targetingValidation = useMemo(() => ({ version: validationVersion }), [validationVersion]);
  const designSelection = useMemo(() => ({ selectedDesign, setSelectedDesign }), [selectedDesign]);
  const trackingValidity = useMemo(() => ({ trackingValid, setTrackingValid }), [trackingValid]);

  useEffect(() => {
    setCampaignId(campaignId);
    setClientId(clientId);
  }, [campaignId, clientId, setCampaignId, setClientId]);

  const consumeLaunchDateFocus = useCallback(() => {
    const pending = launchDateFocusPending.current;
    launchDateFocusPending.current = false;
    return pending;
  }, []);

  const launchDateFocus = useMemo(
    () => ({ version: launchDateFocusVersion, consume: consumeLaunchDateFocus }),
    [launchDateFocusVersion, consumeLaunchDateFocus]
  );

  const handleEditLaunchDate = useCallback(() => {
    launchDateFocusPending.current = true;
    setLaunchDateFocusVersion((version) => version + 1);
    setActiveStep("targeting");
    // PAD-32157 — route the "edit launch date" jump-back through the shared capture path.
    captureStepReached("targeting");
  }, [captureStepReached]);

  const handleRequestValidation = useCallback(
    (step: BuilderStep) => {
      if (!campaignData) return;
      setValidationVersion((version) => version + 1);
      if (step !== "targeting") return;
      const firstInvalid =
        validateCampaignName(campaignData.campaignName ?? "") !== null || !campaignData.startDate
          ? "details"
          : activeAudienceDetails.length === 0 || recipientsAmount < admCapabilities.minRecipients
          ? "audience"
          : typeof campaignData.updateAddressOnMove !== "boolean" || campaignData.returnAddressId == null
          ? "delivery"
          : null;
      if (!firstInvalid) return;
      requestAnimationFrame(() => {
        document
          .querySelector(`[data-targeting-section="${firstInvalid}"]`)
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    },
    [campaignData, activeAudienceDetails.length, recipientsAmount, admCapabilities.minRecipients]
  );

  let content: React.ReactNode;
  if (hasError) {
    content = <BuilderErrorState onRetry={handleRetry} />;
  } else if (!dataLoaded || !campaignData || !client || !country) {
    // The sidebar is always rendered (below); only the content area is skeletonized.
    // Waiting for dataLoaded means we render straight onto the resolved step (e.g.
    // Tracking for a campaign that already has a design) instead of flashing
    // targeting / the "select a design first" empty state first.
    content = <BuilderPageSkeleton />;
  } else {
    const audiences = activeAudienceDetails.length
      ? activeAudienceDetails.map((audience) => audience.name).join(", ")
      : "Pending";

    const launchDate = campaignData.startDate
      ? new Date(Number(campaignData.startDate)).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "Pending";

    const launchDateOutdated = isLaunchDateOutdated(campaignData.startDate);
    // Step state uses activeRoomId for "has a design" (no hydration wait), so the
    // address-box requirement is enforced here at the submit gate instead — by the
    // final step the selected design has hydrated, so its addressBox flag is known.
    const designAddressBoxMissing = lacksAddressBox(addressBoxRequired, selectedDesign);

    content = (
      <SaveStatusProvider onStatus={setSaveStatus}>
        <LaunchDateFocusContext.Provider value={launchDateFocus}>
          <TargetingValidationContext.Provider value={targetingValidation}>
            <DesignSelectionContext.Provider value={designSelection}>
              <TrackingValidityContext.Provider value={trackingValidity}>
                <BuilderShell
                  config={admConfig}
                  campaign={snapshot}
                  activeStep={activeStep}
                  onStepChange={handleStepChange}
                  stepComponents={{ targeting: AdmTargetingStep, design: AdmDesignStep, tracking: AdmTrackingStep }}
                  summary={{
                    totalCost: formatTotal(campaignCostPreview?.total ?? 0),
                    campaignName: campaignData.campaignName || "Pending",
                    launchDate,
                    audiences,
                    format: campaignData.flyerType || "Pending",
                    saveStatus,
                    launchDateWarning: launchDateOutdated,
                    ctaDataCy: ADM_CTA_DATA_CY[activeStep],
                  }}
                  isSubmitBlocked={launchDateOutdated || isReadOnly || designAddressBoxMissing}
                  contentReadOnly={isReadOnly}
                  onEditLaunchDate={handleEditLaunchDate}
                  onPrimaryAction={handleReviewAndFinish}
                  onRequestValidation={handleRequestValidation}
                  headerRight={<UserMenu clientId={clientId} companyName={client?.name} />}
                  topBanner={billingGate.renderBanner("adm-builder-step")}
                />
                {billingGate.drawer}
                <ConfirmEmailModal
                  open={isConfirmEmailOpen}
                  onClose={() => setIsConfirmEmailOpen(false)}
                  onVerified={() => {
                    setIsConfirmEmailOpen(false);
                    setOrderDetailsOpen(true);
                  }}
                />
                <OrderDetailsDrawer
                  open={orderDetailsOpen}
                  onClose={() => setOrderDetailsOpen(false)}
                  isPaymentSkipped={isPaymentSkipped}
                  onGoToPayment={handleGoToPayment}
                  onBookReviewMeeting={() => undefined}
                  topBanner={billingGate.renderBanner("adm-order-details")}
                  onDiscountChoiceChange={setDiscountChoice}
                />
                <CheckoutSidebar show={showCheckout} onClose={() => setShowCheckout(false)}>
                  {showCheckout && (
                    <CheckoutContent
                      campaignId={submitCampaignId}
                      clientId={clientId || ""}
                      breakdown={costBreakdown ?? null}
                      breakdownReady={breakdownReady}
                      couponCode={couponCode}
                      applyReviewDiscount={applyReviewDiscount}
                      countryCode={countryCode}
                      onClose={() => setShowCheckout(false)}
                      // After a successful payment the checkout shows a terminal success screen (no close/finish) and then sends the client out of the builder, so they can't return and pay again (PAD-31453).
                      onPaymentSuccess={() => window.location.replace(getPostSubmitUrl())}
                    />
                  )}
                </CheckoutSidebar>
                <CampaignSubmissionModal
                  isOpen={isCampaignSubmissionModalOpen}
                  onConfirmation={() => {
                    setIsCampaignSubmissionModalOpen(false);
                    window.location.replace(getPostSubmitUrl());
                  }}
                  title="Campaign submitted!"
                  body="We'll review and confirm your campaign soon."
                  icon={SuccessIcon}
                  ctaText="Back to home"
                />
              </TrackingValidityContext.Provider>
            </DesignSelectionContext.Provider>
          </TargetingValidationContext.Provider>
        </LaunchDateFocusContext.Provider>
      </SaveStatusProvider>
    );
  }

  return (
    <div className="flex h-screen w-full overflow-hidden">
      <ThemedOadsSidebar
        baseUrls={{
          platform: CONFIG.PLATFORM_BASE_URL,
          next: CONFIG.NEXT_BASE_URL,
          builder: CONFIG.BUILDER_BASE_URL,
        }}
        clientId={clientId ?? ""}
        client={{
          // Channel visibility (PAD-31735): country + the client's self-serve
          // flag + DM-campaign presence (parity with platform-next).
          country: country?.code,
          isSelfServeEnabled: client?.isSelfServeEnabled ?? true,
          hasDmCoreCampaigns: client?.hasDmCoreCampaigns ?? false,
          isEddmOnly: client?.createdBy === EDDM_SIGNUP,
        }}
        currentPath={pathname}
        footerItems={oadsSidebarFooterItems}
        eddmLogo={client?.createdBy === EDDM_SIGNUP}
        renderLink={renderSidebarLink}
      />
      <main className="flex h-screen min-w-0 flex-1 flex-col overflow-hidden">{content}</main>
    </div>
  );
};
